import axios from "axios";
import apiClient from "@/axios";
import store from "@/store/index";

const { VUE_APP_PREPR_API, VUE_APP_MICROSERVICES_API } = process.env;

const ax = axios.create();
const queryString = `
  _id
  product_name
  product_type
  subscription_options {
    body
  }
  _slug
  header_image {
    _id
    url
  }
  product_icon {
  _id
  url
  }
  product_photo {
    _id
    url
  }
  product_header
  product_introduction
  product_detail_header
  product_details
  product_page_tabs {
    name
    body
    icon {
      url
    }
    image {
      url
    }
  }
  how_it_works_header
  how_it_works_widget {
    number
    icon {
      url
    }
    title
    body
  }
  faq_header
  faqs {
    question
    answer
  }
  testimonials_header
  testimonials {
    author
    body
    rating
  }
  price_string
  price
  usa_price
  eur_price
  aus_price
  can_price
  sku
  usa_sku
  eur_sku
  aus_sku
  can_sku
  currency
  subscription_price_string
  subscription_price
  product_descriptor_list
  doctor_profile_image {
    _id
    url
  }
  doctor_name_and_role
  whats_included {
    panel
    markers
  }
  age_tags {
    body
  }
  gender_tags {
    body
  }
  wellbeing_goal_tags {
    body
  }
  collection_methods {
    _id
    _slug
    name
    description
    price_string
    price
    post_order_text
    icon {
      url
    }
  }
  thumbnail_image {
    url
  }
  testimonials {
    author
    body
    rating
    background {
      url
    }
  }  
  product_type
  doctor_text
  aus_price_string
  can_price_string
  aus_price_string
  eur_price_string
  aus_subscription_price_string
  can_subscription_price_string
  usa_subscription_price_string
  eur_subscription_price_string
`;

export async function getProductData(productId) {
  const res = await ax({
    url: VUE_APP_PREPR_API,
    method: "post",
    data: {
      query: `
          query ProductPage($productPageId: String) {
            ProductPage(id: $productPageId) {
              ${queryString}
            }
          }
        `,
      variables: {
        productPageId: productId,
      },
    },
  });
  return res.data.data.ProductPage;
}

export async function getAllProducts(options = {}) {
  const profileRegion = store.getters?.profile?.profile?.region || "GBR";
  const allowedRegions = ["GBR", "USA", "AUS", "EUR", "CAN", "NZL", "INT"];
  const region = allowedRegions.includes(options?.region) ? options.region : profileRegion;
  const currencyByRegion = {
    GBR: "GBP",
    USA: "USD",
    AUS: "AUD",
    EUR: "EUR",
    CAN: "CAD",
    NZL: "NZD",
    INT: "USD",
  };
  const symbolByCurrency = {
    GBP: "£",
    USD: "$",
    AUD: "A$",
    EUR: "€",
    CAD: "C$",
    NZD: "NZ$",
  };

  const regionPriceKeyByCode = {
    GBR: "price",
    USA: "usa_price",
    AUS: "aus_price",
    EUR: "eur_price",
    CAN: "can_price",
    NZL: "nz_price",
    // Prepr has no row_price field, so the international store prices off the USA one.
    INT: "usa_price",
  };
  const regionSkuKeyByCode = {
    GBR: "sku",
    USA: "usa_sku",
    AUS: "aus_sku",
    EUR: "eur_sku",
    CAN: "can_sku",
    NZL: "nz_sku",
    // row_sku, matching ecommerce's resolvePreprSku. This used to read "sku", which sent
    // international shoppers the UK SKU. row_sku is optional in Prepr, and resolveSku
    // falls back to the base sku when it is unset.
    INT: "row_sku",
  };

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "string" && value.trim().toLowerCase() === "null") return null;
    return value;
  };

  const resolvePrice = (product) => {
    const priceKey = regionPriceKeyByCode[region] || "price";
    return product?.[priceKey] ?? product?.price ?? 0;
  };

  const resolveSku = (product) => {
    const skuKey = regionSkuKeyByCode[region] || "sku";
    return (
      normalizeValue(product?.[skuKey]) ||
      normalizeValue(product?.sku) ||
      null
    );
  };

  const toPriceString = (price, symbol) => {
    if (price === 0) return "FREE";
    return `${symbol || ""}${price / 100}`;
  };

  const toLegacyHealthStoreShape = (product) => {
    // What this product costs here, decided by ecommerce (PreprRegionalPricing) and sent
    // down whole. The local resolvers below are the fallback for a gateway that predates
    // that change; they answer for the base product rather than re-deriving a region,
    // because two places deciding the region independently is the bug this replaces - the
    // page heading said £15 while the payment modal said €15 for the same upgrade.
    const pricing = product?.pricing || null;
    const selectedPrice = pricing ? pricing.price ?? 0 : resolvePrice(product);
    const currencyCode = pricing
      ? pricing.currency
      : currencyByRegion[region] || product?.currency || "USD";
    const selectedSku = pricing ? normalizeValue(pricing.sku) : resolveSku(product);
    const currencySymbol = {
      raw: pricing?.currencySymbol?.raw || symbolByCurrency[currencyCode] || "",
    };
    // Prepr exposes a distinct asset per slot and they are not interchangeable.
    // product_photo is a full-bleed marketing shot, and every Genetic Test product fills
    // it with the same transparent placeholder - using it as the card image is what made
    // the whole Genetic Reports category render blank. Fall back to it only so the store
    // keeps working while the gateway change that adds these fields rolls out.
    const thumbnailUrl =
      product?.thumbnailImageUrls?.[0] || product?.productPhotoUrls?.[0] || "";
    const headerUrl =
      product?.headerImageUrls?.[0] || product?.productPhotoUrls?.[0] || "";
    const requiresShipping = product?.product_type === "Blood Test";

    return {
      _id: product?._id || product?.id || product?._slug,
      _slug: product?._slug,
      slug: product?._slug,
      // Passed on whole, not only folded into variants[0] below. The card checkout hands
      // createOrderObject no variant, so without this it fell back to working the SKU and
      // currency out again from a stand-in country: an international shopper was sent the
      // US SKU in dollars, whatever this card had shown them.
      pricing,
      // What kind of product this is, in the word the details page understands. A genetic
      // report ships nothing - it unlocks traits on a kit the customer already has - so it
      // is a digital upgrade and must skip the address step. Blood tests ship a kit.
      category: requiresShipping ? "Blood Test" : "Digital upgrade",
      product_name: product?.product_name || "",
      product_type: product?.product_type || "Genetic Test",
      product_details: product?.product_description || "",
      shortDescription: product?.product_description || "",
      price: selectedPrice,
      price_string: toPriceString(selectedPrice, currencySymbol.raw),
      purchased: product?.purchased === true,
      includedInWellbeingGoals: false,
      sku: normalizeValue(product?.sku),
      usa_sku: normalizeValue(product?.usa_sku),
      eur_sku: normalizeValue(product?.eur_sku),
      aus_sku: normalizeValue(product?.aus_sku),
      can_sku: normalizeValue(product?.can_sku),
      nz_sku: normalizeValue(product?.nz_sku),
      row_sku: normalizeValue(product?.row_sku),
      usa_price: product?.usa_price ?? null,
      eur_price: product?.eur_price ?? null,
      aus_price: product?.aus_price ?? null,
      can_price: product?.can_price ?? null,
      nz_price: product?.nz_price ?? null,
      currencySymbol,
      variants: selectedSku
        ? [
            {
              sku: selectedSku,
              currency: currencyCode,
              shippable: requiresShipping,
              price: {
                base: selectedPrice,
                isOnSale: false,
              },
              currencySymbol,
            },
          ]
        : [],
      header_image: headerUrl ? [{ url: headerUrl }] : [],
      thumbnail_image: thumbnailUrl ? [{ url: thumbnailUrl }] : [],
      // How the sample is collected, straight from Prepr. The store offers no choice for
      // a digital upgrade - there is only digitalcollection - but PaymentModal still has to
      // put that slug in the cart, because it becomes DIG01 in the canonical SKU. Left
      // empty, checkout refused the order: "Genetic Test requires digitalcollection, but it
      // was not found in Prepr collection_methods".
      collection_methods: Array.isArray(product?.collection_methods) ? product.collection_methods : [],
    };
  };

  const barcode = options?.barcode !== undefined ? options.barcode : (store.getters?.profile?.profile?.barcode || null);

  const preprProductsUrl = `${VUE_APP_MICROSERVICES_API}/legacy/ecommerce/api/prepr/products`;
  const asList = ({ data }) => (Array.isArray(data) ? data : []);
  const preprFetch = (types) => {
    // region is what makes ecommerce resolve the price: each product comes back with a
    // `pricing` block for this store, so nothing here has to choose between the seven
    // regional sku fields and six price fields itself.
    const params = { region, ...(barcode ? { barcode } : {}) };
    const storeList = () => axios.post(preprProductsUrl, types, { params }).then(asList).catch(() => []);
    if (!options?.includePurchased || !barcode) {
      return storeList();
    }
    // The store list leaves out the Health Reports this barcode owns. includePurchased keeps
    // them, flagged `purchased`, so a page can say "you already have this" rather than find
    // nothing. It says what the barcode has bought, so ecommerce serves it only to the
    // signed-in user - hence apiClient, which carries the token. If it is refused, fall back
    // to the store list: owned reports are left out of it, as they always were.
    return apiClient.post(preprProductsUrl, types, { params: { ...params, includePurchased: true } })
      .then(asList)
      .catch(() => storeList());
  };

  // Blood tests are no longer sold, so the store has no Blood Tests category. Customers who
  // have one still see their results under /blood/test.
  const healthReports = await preprFetch(["Genetic Test"]);

  return healthReports.map(toLegacyHealthStoreShape);
}

export async function getEpigeneticProducts(options = {}) {
  try {
    const profileRegion = store.getters?.profile?.profile?.region || "GBR";
    const allowedRegions = ["GBR", "USA", "AUS", "EUR", "CAN", "INT"];
    const region = allowedRegions.includes(options?.region) ? options.region : profileRegion;

    const res = await axios.post(
      `${VUE_APP_MICROSERVICES_API}/legacy/ecommerce/api/prepr/products`,
      ["Epigenetic"],
      { params: { region } },
    );

    const epigeneticProducts = Array.isArray(res.data) ? res.data : [];

    // Transform REST API response to match GraphQL structure. Price, currency, symbol and
    // sku come from ecommerce's `pricing` block so this agrees with the rest of the store;
    // the fallbacks below are for a gateway that predates it.
    const symbolByCurrency = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      AUD: 'A$',
      CAD: 'C$'
    };

    const transformed = epigeneticProducts.map(product => {
      const pricing = product.pricing || null;
      const priceAmount = pricing ? pricing.price ?? 0 : (product.price?.amount || 0);
      const currencyCode = (pricing ? pricing.currency : product.price?.currency) || 'GBP';
      const productSku = (pricing ? pricing.sku : product.sku) || null;
      const currencySymbolRaw = pricing?.currencySymbol?.raw || symbolByCurrency[currencyCode] || '£';
      
      return {
        _id: product.id || product._id,
        product_name: product.name,
        product_type: "Epigenetic",
        _slug: product.slug,
        header_image: product.images?.[0] ? { url: product.images[0] } : null,
        product_icon: product.icon ? { url: product.icon } : null,
        product_photo: product.images?.[0] ? { url: product.images[0] } : null,
        product_header: product.shortDescription || "",
        product_introduction: product.description || "",
        product_detail_header: "",
        product_details: product.description || "",
        price_string: product.price?.formatted || "",
        price: priceAmount,
        subscription_price_string: "",
        subscription_price: 0,
        product_descriptor_list: [],
        thumbnail_image: product.images?.[0] ? { url: product.images[0] } : null,
        age_tags: [{ body: "All-Ages" }],
        gender_tags: [{ body: "Male" }, { body: "Female" }],
        wellbeing_goal_tags: [],
        // As above: the slug has to reach the cart even when there is only one of them.
        collection_methods: Array.isArray(product?.collection_methods) ? product.collection_methods : [],
        subscription_options: [],
        product_page_tabs: [],
        how_it_works_widget: [],
        faqs: [],
        testimonials: [],
        whats_included: [],
        purchased: false,
        includedInWellbeingGoals: false,
        sku: productSku,
        currencySymbol: {
          raw: currencySymbolRaw
        },
        variants: productSku ? [
          {
            sku: productSku,
            currency: currencyCode,
            shippable: true,
            price: {
              base: priceAmount,
              isOnSale: false
            },
            currencySymbol: {
              raw: currencySymbolRaw
            }
          }
        ] : []
      };
    });
    
    return transformed;
  } catch (error) {
    console.error('Error fetching Epigenetic products:', error);
    return [];
  }
}

